// Sopwith 3D - high score table.
//
// Ten entries kept in localStorage. The storage backend is injectable so the
// logic can be tested without a browser.

export interface HighScore {
  score: number;
  level: number;
  mode: string;
  date: string;
  medals: number;
}

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const KEY = 'sopwith3d.highscores';
const MAX = 10;

export class HighScores {
  private entries: HighScore[] = [];

  constructor(private readonly store: KeyValueStore | null) {
    try {
      const raw = store?.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          this.entries = parsed.filter(
            (e): e is HighScore => typeof e === 'object' && e !== null && typeof e.score === 'number',
          );
        }
      }
    } catch {
      this.entries = [];
    }
  }

  get list(): readonly HighScore[] {
    return this.entries;
  }

  /** Insert a score; returns its 1-based rank, or 0 if it did not make the table. */
  add(entry: HighScore): number {
    if (entry.score <= 0) return 0;
    const list = [...this.entries, entry].sort((a, b) => b.score - a.score).slice(0, MAX);
    const rank = list.indexOf(entry) + 1;
    if (rank === 0) return 0;
    this.entries = list;
    try {
      this.store?.setItem(KEY, JSON.stringify(list));
    } catch {
      // Storage may be unavailable (private mode); keep the in-memory copy.
    }
    return rank;
  }

  /** Would this score make the table? */
  qualifies(score: number): boolean {
    if (score <= 0) return false;
    return this.entries.length < MAX || score > this.entries[this.entries.length - 1].score;
  }
}
